// Independent invariant + SOLVABILITY checks over a generated level.
//
// Two layers:
//   - Structural invariants (overlaps, ramp well-formedness) re-derived from the tiles.
//   - A geometry-aware SOLVABILITY check: a player is flooded across the real walkable
//     surface, where curb walls (the exact set the mesh renders) block movement and only
//     genuinely climbable transitions (same level, or a ramp end) are crossable. This is
//     what catches "a curb blocks a slope" — the abstract tile graph looked connected, but
//     the rendered geometry is not traversable.

import { DVEC, type GeneratedLevel } from "./grammar";
import { stepBetween, edgeKey, edgeAxisOf, computeCurbs } from "./geometry";

export interface ValidationReport {
  ok: boolean;
  tiles: number;
  cells: number;
  overlaps: number; // tile pairs sharing positive-area XZ (should be 0)
  rampsWellFormed: boolean; // every ramp spans exactly one elevation level
  // --- solvability (geometry-aware: respects curb walls + ramp slopes) ---
  solvable: boolean; // player can reach the exit from the start
  fullyTraversable: boolean; // every walkable cell reachable from the start
  reachableCells: number;
  curbBlockedTransitions: number; // walkable transitions a curb wrongly walls off (the bug class)
  curbBlockedExamples: string[]; // human-readable "cell -> cell" for the first few
  hasRoom: boolean;
  hasRamp: boolean;
  hasTurn: boolean;
  errors: string[];
}

const key = (cx: number, cz: number): string => `${cx},${cz}`;

export function validateLevel(lvl: GeneratedLevel): ValidationReport {
  const errors: string[] = [];
  const cells = lvl.cells;

  // 1. Overlap check: EXACT float rectangle intersection over every tile pair. Deliberately
  //    does not reuse the generator's cell rounding, so it can catch rounding bugs.
  let overlaps = 0;
  const ts = lvl.tiles;
  for (let i = 0; i < ts.length; i++) {
    for (let j = i + 1; j < ts.length; j++) {
      const a = ts[i];
      const b = ts[j];
      const ix = Math.min(a.x1, b.x1) - Math.max(a.x0, b.x0);
      const iz = Math.min(a.z1, b.z1) - Math.max(a.z0, b.z0);
      if (ix > 0 && iz > 0) overlaps++;
    }
  }
  if (overlaps > 0) errors.push(`${overlaps} overlapping tile pairs`);

  // 2. Ramp well-formedness.
  let rampsWellFormed = true;
  for (const t of lvl.tiles) {
    if (t.ramp && Math.abs(t.ramp.backLevel - t.ramp.frontLevel) !== 1) {
      rampsWellFormed = false;
      errors.push(`ramp tile ${t.id} spans ${t.ramp.backLevel}->${t.ramp.frontLevel}`);
    }
  }

  // 3. SOLVABILITY. Flood a player across walkable surfaces. An edge A->B is crossable iff B
  //    exists, the surfaces meet climbably (stepBetween), AND no curb walls that boundary.
  //    The curb set is the exact same one the mesh renders (single source of truth), so a
  //    curb sitting on a walkable transition (e.g. a ramp end) actually blocks the flood here.
  const { blocked } = computeCurbs(cells);

  // 3a. Direct detector for the bug class: walkable transitions that a curb walls off.
  let curbBlockedTransitions = 0;
  const curbBlockedExamples: string[] = [];
  for (const [k, a] of cells) {
    const [cx, cz] = k.split(",").map(Number);
    for (let d = 0; d < 4; d++) {
      const [dx, dz] = DVEC[d];
      if (dx < 0 || dz < 0) continue; // each undirected edge once
      const b = cells.get(key(cx + dx, cz + dz));
      if (!b) continue;
      if (stepBetween(a, b, edgeAxisOf(d)) && blocked.has(edgeKey(cx, cz, d))) {
        curbBlockedTransitions++;
        if (curbBlockedExamples.length < 6)
          curbBlockedExamples.push(`${k} -> ${cx + dx},${cz + dz}`);
      }
    }
  }
  if (curbBlockedTransitions > 0)
    errors.push(`${curbBlockedTransitions} walkable transition(s) blocked by a curb`);

  // 3b. Player flood over the real geometry.
  const startKeys: string[] = [];
  const exitKeys = new Set<string>(); // the enemy-core chamber cells (the finale objective)
  for (const [k, info] of cells) {
    if (info.kind === "start") startKeys.push(k);
    if (info.kind === "core") exitKeys.add(k);
  }
  const seen = new Set<string>(startKeys);
  const stack = [...startKeys];
  let reachedExit = false;
  while (stack.length) {
    const k = stack.pop()!;
    if (exitKeys.has(k)) reachedExit = true;
    const [cx, cz] = k.split(",").map(Number);
    const a = cells.get(k)!;
    for (let d = 0; d < 4; d++) {
      const [dx, dz] = DVEC[d];
      const nk = key(cx + dx, cz + dz);
      if (seen.has(nk)) continue;
      const b = cells.get(nk);
      if (!b) continue;
      if (!stepBetween(a, b, edgeAxisOf(d))) continue; // cliff / ramp side: not climbable
      if (blocked.has(edgeKey(cx, cz, d))) continue; // curb wall in the way
      seen.add(nk);
      stack.push(nk);
    }
  }
  const reachableCells = seen.size;
  const fullyTraversable = reachableCells === cells.size;
  const hasStart = startKeys.length > 0;
  const hasExit = exitKeys.size > 0;
  const solvable = hasStart && hasExit && reachedExit;
  if (!hasStart) errors.push("no start tile");
  if (!hasExit) errors.push("no core chamber");
  if (hasStart && hasExit && !reachedExit) errors.push("core unreachable from start (player-solvability)");
  if (!fullyTraversable)
    errors.push(`${cells.size - reachableCells} cells unreachable from start`);

  const hasRoom = lvl.tiles.some((t) => t.kind === "room");
  const hasRamp = lvl.tiles.some((t) => t.kind === "ramp");
  const hasTurn = lvl.tiles.some((t) => t.kind === "corner");

  const ok =
    overlaps === 0 &&
    rampsWellFormed &&
    solvable &&
    fullyTraversable &&
    curbBlockedTransitions === 0;

  return {
    ok,
    tiles: lvl.tiles.length,
    cells: cells.size,
    overlaps,
    rampsWellFormed,
    solvable,
    fullyTraversable,
    reachableCells,
    curbBlockedTransitions,
    curbBlockedExamples,
    hasRoom,
    hasRamp,
    hasTurn,
    errors,
  };
}
